import { createSelector } from "@reduxjs/toolkit";
import type { Appstore } from "../../../redux";
import {
  computePayableBalance,
  computeStaffDisplayTotalEventFee,
  derivePaymentStatus,
  getAllowedPaymentMethods,
  getNewPaymentMethodOptions,
  getPrepaidPaymentAmount,
  hasPrepaidPaymentInLedger,
  registrationAlreadyHasPrepaid,
  shouldLockRegistrantPaymentMethod,
  shouldWaiveMainGuestFees,
  shouldWaiveMemberEventFee,
  toPaymentMethodOptions,
  type StaffFormPaymentState,
} from "./utils/participantPaymentRules";
import { shouldUseLateFeesForRegistration } from "../../../utils/invoiceCalculations";
import {
  ensureSelectedOption,
  isGuestRegistrantType,
} from "./hooks/useRegistrantCatalogOptions";
import { filterFirmsForRegistrantType } from "../../../utils/wsgStaffFirms";
import { calculateSelectedSpecialEventFees } from "./specialEventProgramDisplay";

const selectEditParticipant = (state: Appstore) => state.editParticipant;
const selectFirmsCatalog = (state: Appstore) => state.firms;

export const selectEditParticipantMeta = createSelector(
  selectEditParticipant,
  (ep) => ep.meta,
);

export const selectEditParticipantData = createSelector(
  selectEditParticipant,
  (ep) => ep.data,
);

export const selectEditParticipantForm = createSelector(
  selectEditParticipant,
  (ep) => ep.form,
);

export const selectEditParticipantAudit = createSelector(
  selectEditParticipant,
  (ep) => ep.audit,
);

export const selectEditParticipantSave = createSelector(
  selectEditParticipant,
  (ep) => ep.save,
);

export const selectEventData = createSelector(
  selectEditParticipantData,
  (data) => data.eventData,
);

export const selectRegistrationData = createSelector(
  selectEditParticipantData,
  (data) => data.registrationData,
);

export const selectPaymentData = createSelector(
  selectEditParticipantData,
  (data) => data.paymentData,
);

export const selectPayments = createSelector(
  selectEditParticipantData,
  (data) => data.payments,
);

export const selectHasPrepaidPaymentInLedger = createSelector(
  selectPayments,
  (payments) => hasPrepaidPaymentInLedger(payments),
);

export const selectFirmData = createSelector(
  selectEditParticipantData,
  (data) => data.firmData,
);

export const selectIsEditMode = createSelector(
  selectEditParticipantMeta,
  (meta) => meta.isEditMode,
);

export const selectEventId = createSelector(
  selectEditParticipantMeta,
  (meta) => meta.eventId,
);

export const selectRegistrationId = createSelector(
  selectEditParticipantMeta,
  (meta) => meta.registrationId,
);

export const selectVisibleTabs = createSelector(
  selectEditParticipantMeta,
  (meta) => (meta.isEditMode || meta.hasSavedOnce ? meta.tabs : [meta.tabs[0]]),
);

export const selectCanOpenInvoiceTab = createSelector(
  selectEditParticipantMeta,
  selectEditParticipantData,
  (meta, data) =>
    Boolean(data.eventData && data.registrationData?.id) &&
    (meta.isEditMode || meta.hasSavedOnce),
);

export const selectStaffFormState = createSelector(
  selectEditParticipantForm,
  selectHasPrepaidPaymentInLedger,
  (form, hasPrepaidInLedger): StaffFormPaymentState => ({
    guests: form.guests,
    selectedSpecialEventIds: form.selectedSpecialEventIds,
    specialEventGuestsMap: form.specialEventGuestsMap,
    additionalFeeInput: form.additionalFeeInput,
    hasPrepaidPaymentInLedger: hasPrepaidInLedger,
  }),
);

export const selectTotalEventFee = createSelector(
  selectRegistrationData,
  selectEventData,
  selectStaffFormState,
  (registrationData, eventData, staffFormState) => {
    if (!registrationData) {
      return 0;
    }
    return computeStaffDisplayTotalEventFee(
      registrationData,
      eventData,
      staffFormState,
    );
  },
);

export const selectPayableBalance = createSelector(
  selectRegistrationData,
  selectEventData,
  selectStaffFormState,
  (registrationData, eventData, staffFormState) => {
    if (!registrationData) {
      return 0;
    }
    return computePayableBalance(registrationData, eventData, staffFormState);
  },
);

export const selectRegistrantPaymentMethodDisabled = createSelector(
  selectIsEditMode,
  selectRegistrationData,
  selectPayments,
  selectEditParticipantForm,
  (isEditMode, registrationData, payments, form) =>
    shouldLockRegistrantPaymentMethod({
      isEditMode,
      registration: registrationData,
      payments,
      autoPrepaidLocked: form.autoPrepaidLocked,
    }),
);

export const selectPaymentMethodOptions = createSelector(
  selectRegistrationData,
  selectEventData,
  selectStaffFormState,
  (registrationData, eventData, staffFormState) => {
    if (!registrationData) {
      return [];
    }
    return toPaymentMethodOptions(
      getAllowedPaymentMethods(registrationData, eventData, staffFormState),
    );
  },
);

export const selectDerivedPaymentStatus = createSelector(
  selectRegistrationData,
  selectEventData,
  selectStaffFormState,
  (registrationData, eventData, staffFormState) => {
    if (!registrationData) {
      return "pending" as const;
    }
    return derivePaymentStatus(registrationData, eventData, staffFormState);
  },
);

export const selectNewPaymentMethodOptions = createSelector(
  selectRegistrationData,
  selectEventData,
  selectStaffFormState,
  selectPayments,
  (registrationData, eventData, staffFormState, payments) => {
    if (!registrationData) {
      return [];
    }
    return toPaymentMethodOptions(
      getNewPaymentMethodOptions(registrationData, eventData, staffFormState, {
        hasPrepaidPayment: registrationAlreadyHasPrepaid(
          registrationData,
          payments,
        ),
      }),
    );
  },
);

export const selectPrepaidPaymentAmount = createSelector(
  selectRegistrationData,
  selectEventData,
  (registrationData, eventData) => {
    if (!registrationData) {
      return 0;
    }
    return getPrepaidPaymentAmount(eventData, registrationData);
  },
);

export const selectHasPrepaidPayment = createSelector(
  selectRegistrationData,
  selectPayments,
  (registrationData, payments) =>
    registrationAlreadyHasPrepaid(registrationData, payments),
);

export const selectIsOrWillBePrepaid = createSelector(
  selectHasPrepaidPayment,
  selectEditParticipantForm,
  (hasPrepaid, form) => hasPrepaid || form.newPaymentMethod === "prepaid",
);

export const selectRegistrantFieldsDisabled = createSelector(
  selectIsEditMode,
  selectEditParticipantForm,
  (isEditMode, form) => isEditMode && !form.isRegistrantModifyMode,
);

export const selectSelectedSpecialEventIdSet = createSelector(
  selectEditParticipantForm,
  (form) => new Set(form.selectedSpecialEventIds),
);

export const selectSpecialEventPrograms = createSelector(
  selectEventData,
  (eventData) => {
    if (!eventData?.program?.length) {
      return [];
    }
    return eventData.program
      .map((program) => ({
        ...program,
        programId: program.id,
      }))
      .filter((p): p is typeof p & { programId: string } =>
        Boolean(p.requiredRegistration && p.programId),
      );
  },
);

export const selectSelectedSpecialEventFees = createSelector(
  selectSpecialEventPrograms,
  selectEditParticipantForm,
  (programs, form) => {
    if (form.selectedSpecialEventIds.length === 0) {
      return null;
    }
    return calculateSelectedSpecialEventFees(
      programs,
      form.selectedSpecialEventIds,
      form.specialEventGuestsMap,
    );
  },
);

export const selectFeeHighlights = createSelector(
  selectEventData,
  selectRegistrationData,
  selectEditParticipantForm,
  selectSelectedSpecialEventFees,
  selectIsOrWillBePrepaid,
  (
    eventData,
    registrationData,
    form,
    selectedSpecialEventFees,
    isOrWillBePrepaid,
  ) => {
    const isLateRegistration =
      !!eventData &&
      !!registrationData &&
      shouldUseLateFeesForRegistration(eventData, registrationData);
    const isGuestAttendee =
      registrationData?.attendeeType?.toLowerCase() === "guest";
    const formGuests = Array.isArray(form.guests) ? form.guests : [];
    const hasGuests = formGuests.length > 0 || isGuestAttendee;
    const isStaff = registrationData?.attendeeType === "staff";
    const memberFeeIsWaived =
      !registrationData ||
      isOrWillBePrepaid ||
      shouldWaiveMemberEventFee(registrationData);
    const guestFeeIsWaived =
      !registrationData ||
      isStaff ||
      shouldWaiveMainGuestFees(registrationData);
    const regularMemberFee =
      eventData?.fee?.memberFee ?? eventData?.fee?.fee ?? 0;
    const lateMemberFee =
      eventData?.fee?.lateFee ?? regularMemberFee;
    const regularGuestFee = eventData?.fee?.guestFee ?? 0;
    const lateGuestFee =
      eventData?.fee?.guestLateFee ?? regularGuestFee;
    const memberFee =
      !memberFeeIsWaived &&
      !isLateRegistration &&
      regularMemberFee > 0;
    const lateMemberFeeIsCharged =
      !memberFeeIsWaived &&
      isLateRegistration &&
      lateMemberFee > 0;
    const guestFee =
      !guestFeeIsWaived &&
      hasGuests &&
      !isLateRegistration &&
      regularGuestFee > 0;
    const guestLateFee =
      !guestFeeIsWaived &&
      hasGuests &&
      isLateRegistration &&
      lateGuestFee > 0;
    const specialEventFee =
      !isStaff && (selectedSpecialEventFees?.eventFee ?? 0) > 0;
    const specialEventGuestFee =
      !isStaff && (selectedSpecialEventFees?.guestFee ?? 0) > 0;

    return {
      memberFee,
      lateMemberFee: lateMemberFeeIsCharged,
      guestFee,
      guestLateFee,
      specialEventFee,
      specialEventGuestFee,
      lateFeeDate: lateMemberFeeIsCharged || guestLateFee,
    };
  },
);

export const selectEditParticipantCatalog = createSelector(
  selectEditParticipant,
  (ep) => ep.catalog,
);

/** Firm options come live from the firms catalog so they appear as soon as firms load. */
export const selectOrganizationOptions = createSelector(
  selectEditParticipantCatalog,
  selectRegistrationData,
  selectFirmsCatalog,
  (catalog, registrationData, firms) => {
    const isGuest = isGuestRegistrantType(registrationData?.attendeeType);
    if (isGuest) {
      return ensureSelectedOption(
        catalog.organizationOptions,
        registrationData?.firmId,
        registrationData?.firmName,
      );
    }

    const firmOptions = filterFirmsForRegistrantType(
      Array.isArray(firms) ? firms : [],
      registrationData?.attendeeType,
    );
    return ensureSelectedOption(
      firmOptions,
      registrationData?.firmId,
      registrationData?.firmName,
    );
  },
);

export const selectOrganizationLabel = createSelector(
  selectRegistrationData,
  selectEditParticipantCatalog,
  (registrationData, catalog) =>
    isGuestRegistrantType(registrationData?.attendeeType)
      ? "Company"
      : catalog.organizationLabel || "Firm",
);

export const selectOrganizationValue = createSelector(
  selectRegistrationData,
  (registrationData) => registrationData?.firmId ?? null,
);

export const selectRegistrantCatalogValue = createSelector(
  selectRegistrationData,
  (registrationData) => registrationData?.personId ?? null,
);

export const selectIsRegistrationCancelled = createSelector(
  selectRegistrationData,
  (registrationData) =>
    registrationData?.status === "cancelled" ||
    Boolean(registrationData?.cancelRegistrationDate),
);

export const selectAttendeeDisplayName = createSelector(
  selectRegistrationData,
  (registrationData) =>
    [registrationData?.personName, registrationData?.personLastName]
      .filter(Boolean)
      .join(" ")
      .trim(),
);

export const selectNewPaymentDate = createSelector(
  selectEditParticipantForm,
  (form) =>
    form.newPaymentDateMs != null ? new Date(form.newPaymentDateMs) : null,
);
